import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, ChevronLeft, ChevronRight, History, Eye } from "lucide-react";
import { format } from "date-fns";
import { useAdminAuditLogs, type AuditLog, type AuditLogSortField } from "@/hooks/useAdminAuditLogs";
import { AuditLogDetailsDialog } from "./AuditLogDetailsDialog";
import { AuditLogColumnFilters } from "./AuditLogColumnFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { generateAuditSummary } from "./auditLogSummary";

export function AuditLogCard() {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const {
    logs,
    totalCount,
    isLoading,
    currentPage,
    setCurrentPage,
    totalPages,
    logsPerPage,
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    sortField,
    sortDirection,
    handleSort,
  } = useAdminAuditLogs();

  const startIndex = (currentPage - 1) * logsPerPage + 1;
  const endIndex = Math.min(currentPage * logsPerPage, totalCount);

  const SortableHeader = ({
    field,
    children,
    className,
  }: {
    field: AuditLogSortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={cn("cursor-pointer hover:bg-muted/50 transition-colors", className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn(
            "h-3 w-3",
            sortField === field ? "text-secondary" : "text-muted-foreground"
          )}
        />
      </div>
    </TableHead>
  );

  return (
    <>
      <AuditLogDetailsDialog
        log={selectedLog}
        open={!!selectedLog}
        onOpenChange={(open) => !open && setSelectedLog(null)}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4 text-secondary" />
                Activity Log
              </CardTitle>
              <CardDescription>Track admin actions on users</CardDescription>
            </div>

            <AuditLogColumnFilters
              filterTags={filterTags}
              addFilter={addFilter}
              removeFilter={removeFilter}
              clearAllFilters={clearAllFilters}
            />
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted">
                  <SortableHeader field="created_at">Date</SortableHeader>
                  <SortableHeader field="admin">Admin</SortableHeader>
                  <SortableHeader field="action">Action</SortableHeader>
                  <TableHead>Summary</TableHead>
                  <SortableHeader field="target">Target User</SortableHeader>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="h-9">
                      <TableCell className="py-1"><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell className="py-1"><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell className="py-1"><Skeleton className="h-5 w-14" /></TableCell>
                      <TableCell className="py-1"><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell className="py-1"><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell className="py-1"><Skeleton className="h-7 w-7" /></TableCell>
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No activity logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="h-9">
                      <TableCell className="py-1 text-xs whitespace-nowrap">
                        {format(new Date(log.created_at), "MMM d, h:mm a")}
                      </TableCell>
                      <TableCell className="py-1 text-sm whitespace-nowrap">
                        {log.admin_name || "Unknown"}
                      </TableCell>
                      <TableCell className="py-1">
                        <Badge
                          variant={log.action === "delete" ? "destructive" : "secondary"}
                          className="text-xs"
                        >
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-1 text-xs text-muted-foreground">
                        {generateAuditSummary(log)}
                      </TableCell>
                      <TableCell className="py-1 text-sm whitespace-nowrap">
                        {log.target_name || (
                          (log.details as Record<string, unknown>)?.deleted_user 
                            ? (((log.details as Record<string, unknown>).deleted_user as Record<string, unknown>)?.name as string) || "Unknown"
                            : <span className="text-muted-foreground italic">Deleted User</span>
                        )}
                      </TableCell>
                      <TableCell className="py-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalCount > 0 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-muted-foreground">
                {totalPages > 1 
                  ? `Showing ${startIndex} to ${endIndex} of ${totalCount} logs`
                  : `Showing ${totalCount} logs of ${totalCount}`
                }
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
